import { IPromise } from 'angular';
import { Messages } from '../../codegen/Localize';
import { IDetailFetcher } from '../fetchers/IDetailFetcher';
import { IListFetcher } from '../fetchers/IListFetcher';
import { RequestHelper } from '../helpers/RequestHelper';
import { ResultHelper } from '../helpers/ResultHelper';
import { ServerApiMapper } from '../helpers/ServerModelHelper';
import { MetricMapper } from '../mappers/MetricMapper';
import { MetricDefinition } from '../models/ContentDefinitions';
import { IMetricInfo } from '../models/IMetricInfo';
import { Module } from '../Module';
import { AddTagsToMetricsRequest } from '../ServerApi/requests/AddTagsToMetricsRequest';
import { DeleteMetricsRequest } from '../ServerApi/requests/DeleteMetricsRequest';
import { GetFieldValuesRequest } from '../ServerApi/requests/GetFieldValuesRequest';
import { GetMetricActionsRequest } from '../ServerApi/requests/GetMetricActionsRequest';
import { GetMetricsRequest } from '../ServerApi/requests/GetMetricsRequest';
import { MarkRecentlyViewedRequest, RecentlyViewedObjectType } from '../ServerApi/requests/MarkRecentlyViewedRequest';
import { MarkViewedRequest, ViewedObjectType } from '../ServerApi/requests/MarkViewedRequest';
import { MoveMetricsToProject } from '../ServerApi/requests/MoveMetricsToProjectRequest';
import { RemoveTagsFromMetricsRequest } from '../ServerApi/requests/RemoveTagsFromMetricsRequest';
import { RenameMetricRequest } from '../ServerApi/requests/RenameMetricRequest';
import { IMetricDescriptionParams, SetMetricDescriptionRequest } from '../ServerApi/requests/SetMetricDescriptionRequest';
import { SetMetricsOwnerRequest } from '../ServerApi/requests/SetMetricsOwnerRequest';
import { SetMetricsSuspendedStateRequest } from '../ServerApi/requests/SetMetricsSuspendedStateRequest';
import { IFieldValue } from '../ServerApi/types/IFieldValue';
import { IMetricActions } from '../ServerApi/types/IMetricActions';
import { IResult } from '../ServerApi/types/IResponse';
import { ErrorNotifyingServer } from '../services/ErrorNotifyingServer';
import { FetcherFactory } from '../services/FetcherFactory';
import { ServerService } from '../services/ServerService';
import { ToasterService, ToastType } from '../services/ToasterService';
import { FieldValuesQuery } from './FieldValuesQuery';
import { IActionResource, IDetailResource, IFieldValuesResource, IListResource } from './Resource';
import { ResourceQuery } from './ResourceQuery';

export class Metrics implements IListResource<IMetricInfo>, IFieldValuesResource, IActionResource<IMetricActions>, IDetailResource<IMetricInfo> {

  public static $inject = [
    '$q',
    'ErrorNotifyingServer',
    'ToasterService',
    'FetcherFactory'
  ];

  private server: ServerService;
  private listFetcher: IListFetcher<IMetricInfo>;
  private allQuery: ResourceQuery<IMetricInfo>;
  private fieldValuesFetcher: IListFetcher<IFieldValue>;
  private detailFetcher: IDetailFetcher<IMetricInfo>;

  constructor(
    private $q: ng.IQService,
    private ErrorNotifyingServer: ErrorNotifyingServer,
    private toaster: ToasterService,
    FetcherFactory: FetcherFactory
  ) {
    this.server = ErrorNotifyingServer.getServerService();
    this.listFetcher = FetcherFactory.getServerResourceFetcher(GetMetricsRequest, ServerApiMapper.metrics);
    this.allQuery = new ResourceQuery(this.listFetcher);
    this.fieldValuesFetcher = FetcherFactory.getServerResourceFetcher(GetFieldValuesRequest, ServerApiMapper.fieldValues);
    this.detailFetcher = FetcherFactory.getServerDetailFetcher('getMetric', MetricMapper.mapDetail);
  }

  public all(): ResourceQuery<IMetricInfo> {
    return this.allQuery;
  }

  public valuesForField(field: string): FieldValuesQuery {
    return new FieldValuesQuery(this.fieldValuesFetcher, field, 'metric');
  }

  public getById(id: string): IPromise<IMetricInfo> {
    return this.detailFetcher.fetchById(id);
  }

  // Actions

  public actions(id: string): IPromise<IMetricActions> {
    return this.server.sendRequest(new GetMetricActionsRequest(id));
  }

  public del(ids: string[]): IPromise<IResult> {
    return this.ErrorNotifyingServer.sendRequestsAndDelay(
      RequestHelper.buildRequests(ids, (idsBlock) => new DeleteMetricsRequest(idsBlock)),
      ResultHelper.combineResults);
  }

  public setOwner(ids: string[], ownerId: string): IPromise<IResult> {
    return this.ErrorNotifyingServer.sendRequestsAndDelay(
      RequestHelper.buildRequests(ids, (idsBlock) => new SetMetricsOwnerRequest(idsBlock, ownerId)),
      ResultHelper.combineResults);
  }

  public setMetricDescription = async (args: IMetricDescriptionParams): Promise<IResult> => {
    return this.ErrorNotifyingServer
      .sendRequestAndDelay(new SetMetricDescriptionRequest(args))
      .then(r => this.toast(r, Messages.editDescriptionSuccessfulWithWait(), Messages.editDescriptionFailure()));
  }

  public rename(id: string, name: string): IPromise<IResult> {
    return this.ErrorNotifyingServer.sendRequestAndDelay(new RenameMetricRequest(id, name));
  }

  public moveToProject(targetProjectId: string, metricIds: string[]): IPromise<IResult> {
    return this.ErrorNotifyingServer.sendRequestsAndDelay(
      RequestHelper.buildRequests(metricIds,
        (idsBlock) => new MoveMetricsToProject({
          ids: idsBlock,
          projectId: targetProjectId
        })),
      ResultHelper.combineResults);
  }

  public addTags(ids: string[], tags: string[]): IPromise<IResult> {
    return (tags.length > 0)
      ? this.ErrorNotifyingServer.sendRequestsAndDelay(
          RequestHelper.buildRequests2(ids, tags,
            (idsBlock, tagsBlock) => new AddTagsToMetricsRequest(idsBlock, tagsBlock)),
          ResultHelper.combineResults)
      : this.$q.when({});
  }

  public removeTags(ids: string[], tags: string[]): IPromise<IResult> {
    return (tags.length > 0)
      ? this.ErrorNotifyingServer.sendRequestsAndDelay(
          RequestHelper.buildRequests2(ids, tags,
            (idsBlock, tagsBlock) => new RemoveTagsFromMetricsRequest(idsBlock, tagsBlock)),
          ResultHelper.combineResults)
      : this.$q.when({});
  }

  public changeTags(ids: string[], tagsToAdd: string[], tagsToRemove: string[]): IPromise<IResult> {
    return this.removeTags(ids, tagsToRemove)
      .then((result: IResult) => (ResultHelper.hasErrors(result) ? result : this.addTags(ids, tagsToAdd)));
  }

  public static isMetricInfo(item: any): item is IMetricInfo {
    return item &&
           item.contentType &&
           item.contentType === MetricDefinition.contentType &&
           item.hasOwnProperty('connectedView');
  }

  public markRecentlyViewed(id: string): IPromise<IResult> {
    return this.server.sendRequest(new MarkRecentlyViewedRequest({
      objectId: id,
      objectType: RecentlyViewedObjectType.Metric
    }));
  }

  public markViewed(id: string): IPromise<IResult> {
    return this.server.sendRequest(new MarkViewedRequest({
      objectId: id,
      objectType: ViewedObjectType.Metric
    }));
  }

  public resumeMetrics(ids: string[]): IPromise<IResult> {
    return this.server.sendRequest(new SetMetricsSuspendedStateRequest(false, ids));
  }

  private toast(result: IResult, successMessage: string, failureMessage: string): any {
    if (result.errors && result.errors.length > 0) {
      this.toaster.pop({
        type: ToastType.Error,
        text: failureMessage
      });
    } else {
      this.toaster.pop({
        type: ToastType.Info,
        text: successMessage
      });
    }
  }
}

Module.instance().service('Metrics', Metrics);
